import React, { useState, useRef, useEffect, useCallback } from 'react';
import { gsap } from 'gsap';
import { CustomEase } from 'gsap/CustomEase';
import './ContactComponent.css';
import ContactSectionHome from '../../HomePage/ContactSectionHome/ContactSectionHome';
import ReactModal from 'react-modal';
import { useRecaptcha } from '../../../hooks/useRecaptcha';
import { useTranslation } from 'react-i18next';

const Modal = ReactModal as any;

interface ContactComponentProps {
  isOpen: boolean;
  onClose: () => void;
}

const ContactComponent: React.FC<ContactComponentProps> = ({
  isOpen,
  onClose,
}) => {
  const navRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const bgPanelsRef = useRef<HTMLDivElement[]>([]);
  const fadeTargetsRef = useRef<HTMLAnchorElement[]>([]);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const { isLoaded, isReady } = useRecaptcha();

  const openNav = useCallback(() => {
    const tl = gsap.timeline();
    if (!modalContentRef.current) return;

    if (
      !navRef.current ||
      !menuRef.current ||
      !overlayRef.current ||
      bgPanelsRef.current.some((panel) => !panel) ||
      fadeTargetsRef.current.some((target) => !target)
    ) {
      return;
    }

    tl.clear()
      .set(navRef.current, { display: 'block' })
      .fromTo(
        menuRef.current,
        { xPercent: 100 },
        { xPercent: 0, duration: 0.7, ease: 'main' },
        '<'
      )
      .fromTo(
        overlayRef.current,
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 0.7, ease: 'main' },
        '<'
      )
      .fromTo(
        bgPanelsRef.current,
        { xPercent: 101 },
        { xPercent: 0, stagger: 0.12, duration: 0.575, ease: 'power4.out' },
        '<'
      )
      .fromTo(
        fadeTargetsRef.current,
        { autoAlpha: 0, yPercent: 50 },
        { autoAlpha: 1, yPercent: 0, stagger: 0.04, duration: 0.7, ease: 'main' },
        '<+=0.2'
      )
      .set(modalContentRef.current, { xPercent: 100, autoAlpha: 0 })
      .to(modalContentRef.current, {
        xPercent: 0,
        autoAlpha: 1,
        duration: 0.7,
        ease: 'main',
      }, 0);
  }, []);

  const closeNav = useCallback(() => {
    const tl = gsap.timeline();
  
    if (!modalContentRef.current) return;
    if (!navRef.current || !menuRef.current || !overlayRef.current) return;
  
    tl.clear()
      .to(overlayRef.current, { autoAlpha: 0, duration: 0.7, ease: 'main' })
      .to(menuRef.current, { xPercent: 100, duration: 0.7, ease: 'main' }, '<')
      .set(navRef.current, { display: 'none' });
  }, []);

  useEffect(() => {
    gsap.registerPlugin(CustomEase);
    gsap.set('.nav', { display: 'none' });

    CustomEase.create('main', '0.65, 0.01, 0.05, 0.99');

    gsap.defaults({
      ease: 'main',
      duration: 0.7,
    });
  }, []);

  useEffect(() => {
    if (isOpen) {
      openNav();
    } else {
      closeNav();
    }
  }, [isOpen, openNav, closeNav]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    },
    [isOpen, onClose]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel={t('contact.modal.label', 'Contact Modal')}
      className="contact-modal"
      overlayClassName="contact-modal-overlay"
      ariaHideApp={false}
      closeTimeoutMS={700}
      style={{
        overlay: {
          zIndex: 1000,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        },
        content: {
          zIndex: 1001,
        }
      }}
    >
      <div ref={modalContentRef} className="modal-content-wrapper">
        <button 
          onClick={onClose}
          className="modal-close-button"
          aria-label={t('contact.modal.close', 'Fermer')}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 24 24"
          >
            <path
              fill="currentColor"
              d="m12 13.4l-2.9 2.9q-.275.275-.7.275t-.7-.275t-.275-.7t.275-.7l2.9-2.9l-2.9-2.875q-.275-.275-.275-.7t.275-.7t.7-.275t.7.275l2.9 2.9l2.875-2.9q.275-.275.7-.275t.7.275q.3.3.3.713t-.3.687L13.375 12l2.9 2.9q.275.275.275.7t-.275.7q-.3.3-.712.3t-.688-.3z"
            />
          </svg>
        </button>
        <nav className="menu">
          <div className="menu-bg">
            <div className="bg-panel first"></div>
            <div className="bg-panel second"></div>
            <div className="bg-panel"></div>
          </div>
          <div className="menu-inner">
            <ContactSectionHome />
          </div>
        </nav>
      </div>
    </Modal>
  );
};

export default ContactComponent;